import { z } from 'zod';

/**
 * What the plain-voice pass hands back for one finding: the reader-facing prose, reworded. The
 * pass lays it over the finding field by field and by index, so a list here must match the
 * finding's list one-for-one — an entry cannot be added, dropped or reordered, only reworded. Every
 * list is optional: an omitted one leaves the finding's own text in place, which is the safe
 * default for a pass whose whole contract is "nothing deleted".
 *
 * Deliberately no length floors beyond non-empty: a floor breach here would discard the rewrite for
 * the whole finding, and the fallback is the authored prose, not a probe.
 */
export const plainVoiceDescriptionBlockSchema = z.object({
  lead: z.string().min(1),
  text: z.string().min(1),
  note: z.string().min(1).optional(),
});

export const plainVoiceSourceSchema = z.object({
  label: z.string().min(1),
  statement: z.string().min(1),
});

export const plainVoiceAlternativeSchema = z.object({
  statement: z.string().min(1),
  note: z.string().min(1),
});

export const plainVoiceRecommendationSchema = z.object({
  businessTitle: z.string().min(1),
  detail: z.string().min(1),
  steps: z.array(z.string().min(1)).optional(),
  impact: z.string().min(1),
  successCriterion: z.string().min(1).optional(),
});

export const plainVoiceViewSchema = z.object({
  findingId: z.string().min(1),
  businessStatement: z.string().min(1),
  caveat: z.string().min(1).optional(),
  headline: z.string().min(1),
  description: z.array(plainVoiceDescriptionBlockSchema).optional(),
  sources: z.array(plainVoiceSourceSchema).optional(),
  alternatives: z.array(plainVoiceAlternativeSchema).optional(),
  recommendations: z.array(plainVoiceRecommendationSchema).optional(),
});

export const plainVoiceResultSchema = z.object({
  view: plainVoiceViewSchema,
});

export type PlainVoiceView = z.infer<typeof plainVoiceViewSchema>;
export type PlainVoiceResult = z.infer<typeof plainVoiceResultSchema>;
